import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from 'lucide-react';

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message, type = 'info', duration = 4000) => {
    if (!message) return;
    const id = Date.now() + Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, type, duration }]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  }, [removeToast]);

  // Override window.alert to automatically show custom toasts
  useEffect(() => {
    const originalAlert = window.alert;
    window.alert = (msg) => {
      const text = String(msg ?? '');
      if (!text) return;
      const lower = text.toLowerCase();
      let type = 'info';
      if (
        lower.includes('error') ||
        lower.includes('fail') ||
        lower.includes('not match') ||
        lower.includes('required') ||
        lower.includes('already') ||
        lower.includes('invalid')
      ) {
        type = 'error';
      } else if (
        lower.includes('success') ||
        lower.includes('registered') ||
        lower.includes('submitted') ||
        lower.includes('uploaded')
      ) {
        type = 'success';
      }
      showToast(text, type);
    };

    return () => {
      window.alert = originalAlert;
    };
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, removeToast }}>
      {children}
      {/* Bottom Right Toast Container */}
      <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3 max-w-sm w-full pointer-events-none px-4 sm:px-0">
        <AnimatePresence>
          {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const ToastItem = ({ toast, onClose }) => {
  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-rose-400 shrink-0" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />;
      case 'info':
      default:
        return <Info className="w-5 h-5 text-cyan-400 shrink-0" />;
    }
  };

  const getBorderColor = () => {
    switch (toast.type) {
      case 'success':
        return 'border-emerald-500/40 bg-slate-900/95 text-emerald-200 shadow-[0_0_20px_rgba(16,185,129,0.15)]';
      case 'error':
        return 'border-rose-500/40 bg-slate-900/95 text-rose-200 shadow-[0_0_20px_rgba(244,63,94,0.15)]';
      case 'warning':
        return 'border-amber-500/40 bg-slate-900/95 text-amber-200 shadow-[0_0_20px_rgba(245,158,11,0.15)]';
      case 'info':
      default:
        return 'border-cyan-500/40 bg-slate-900/95 text-cyan-200 shadow-[0_0_20px_rgba(6,182,212,0.15)]';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.9, x: 20 }}
      animate={{ opacity: 1, y: 0, scale: 1, x: 0 }}
      exit={{ opacity: 0, x: 60, scale: 0.9 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className={`pointer-events-auto relative flex items-start gap-3 p-4 rounded-2xl border backdrop-blur-xl shadow-2xl ${getBorderColor()}`}
    >
      <div className="mt-0.5">{getIcon()}</div>
      <div className="flex-1 text-sm font-sans font-medium text-slate-100 leading-snug break-words">
        {toast.message}
      </div>
      <button
        onClick={onClose}
        className="text-slate-400 hover:text-white transition-colors p-1 -mr-1 -mt-1 rounded-lg hover:bg-white/10"
      >
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
};
